import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertOctagon, Send, Loader2, Lock, MessageCircle, Flag, Star, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import CertifiedBadge from "@/components/CertifiedBadge";
import GoldBadge from "@/components/GoldBadge";
import LifetimeMedal from "@/components/LifetimeMedal";

const TR = {
  sk: {
    title: "POMOOOC",
    subtitle: "Tvoja SOS výzva — celá komunita ti pomôže",
    desc: "Klikneš na červené tlačidlo, popíšeš svoj problém a všetkým profesionálom v zvolenom odbore pípne telefón. Niekto ti rýchlo odpovie. Vzájomná pomoc, 24/7.",
    pushNotice: "ℹ️ Push notifikácie (pípanie telefónu) budú plne funkčné až po vydaní Tigik aplikácie v Google Play — už čoskoro. Zatiaľ uvidíš nové SOS výzvy hneď po obnovení stránky.",
    placeholder: "Popíš svoj problém — buď konkrétny (zváračka, materiál, hrúbka, čo si vyskúšal). Čím viac detailov, tým rýchlejšie ti pomôžu.",
    areaLabel: "Komu posielaš?",
    areaHint: "Vyber odbor — pípne len profíkom z tejto oblasti, ktorí hovoria tvojím jazykom",
    levelLabel: "Tvoja úroveň",
    levelHint: "Otázka pôjde profikom rovnakej alebo vyššej úrovne",
    areas: { orbital: "Orbital zváračom", tig: "TIG zváračom", pipefitter: "Potrubárom", prep: "Prípravárom", other: "Ostatné profesie" },
    levels: { beginner: "Začiatočník", experienced: "Pokročilý", expert: "Profík / Expert" },
    send: "POSLAŤ SOS",
    sending: "Posielam...",
    minChars: "Napíš aspoň 5 znakov.",
    sent: "SOS odoslané! Pípne všetkým aktívnym zváračom — niekto ti rýchlo pomôže.",
    error: "Nepodarilo sa odoslať SOS.",
    recent: "Najnovšie SOS výzvy",
    noSos: "Zatiaľ žiadne aktívne SOS. Buď prvý ak potrebuješ pomoc!",
    replies: "odpovedí",
    reply: "Odpovedať",
    replyPlaceholder: "Napíš ako mu pomôžeš...",
    replySend: "Odoslať",
    replied: "Odpoveď odoslaná!",
    yourSos: "Tvoja výzva",
    reportTitle: "Nahlásiť túto odpoveď adminovi (zneužitie / sranda)",
    reportConfirm: "Naozaj nahlásiť túto odpoveď adminovi? Použi to len ak si niekto z teba robí žarty alebo je hrubý.",
    reported: "Nahlásené adminovi.",
    locked_start: "Online pomoc od komunity",
    locked_start_desc: "Keby si mal ACTIVE alebo PRO, máš úplnú podporu od ostatných užívateľov v reálnom čase. Klikneš, popíšeš problém, pípne celá komunita.",
    locked_cta: "Aktivovať ACTIVE",
    locked_free_desc: "Predplať si Tigik a získaj okamžitú SOS pomoc od stoviek zváračov v komunite.",
    moments_ago: "pred chvíľou",
  },
  en: {
    title: "HEEELP",
    subtitle: "Your SOS call — the entire community helps you",
    desc: "Click the red button, describe your problem and the phone of every pro in the chosen field beeps. Someone will reply fast. Mutual help, 24/7.",
    pushNotice: "ℹ️ Push notifications (phone beep) will be fully working once the Tigik app launches on Google Play — coming soon. For now, new SOS requests appear after page refresh.",
    placeholder: "Describe your problem — be specific (machine, material, thickness, what you tried). More detail = faster help.",
    areaLabel: "Who to send to?",
    areaHint: "Pick a field — only pros from this area get pinged",
    levelLabel: "Your level",
    levelHint: "Question routes to pros of equal or higher level",
    areas: { orbital: "Orbital welders", tig: "TIG welders", pipefitter: "Pipefitters", prep: "Preppers", other: "Other trades" },
    levels: { beginner: "Beginner", experienced: "Experienced", expert: "Pro / Expert" },
    send: "SEND SOS",
    sending: "Sending...",
    minChars: "Type at least 5 characters.",
    sent: "SOS sent! Every active welder's phone beeped — someone will help quickly.",
    error: "Could not send SOS.",
    recent: "Recent SOS calls",
    noSos: "No active SOS yet. Be the first if you need help!",
    replies: "replies",
    reply: "Reply",
    replyPlaceholder: "Write how you'll help...",
    replySend: "Send",
    replied: "Reply sent!",
    yourSos: "Your call",
    reportTitle: "Report this reply to admin (abuse / joke)",
    reportConfirm: "Really report this reply to admin? Use only if someone is mocking you or being rude.",
    reported: "Reported to admin.",
    locked_start: "Live help from the community",
    locked_start_desc: "If you had ACTIVE or PRO, you'd get full real-time support from other users. Click, describe the problem, the whole community is alerted.",
    locked_cta: "Activate ACTIVE",
    locked_free_desc: "Subscribe to Tigik for instant SOS help from hundreds of welders in the community.",
    moments_ago: "moments ago",
  },
  cs: {
    title: "POMOOOC",
    subtitle: "Tvoje SOS výzva — celá komunita ti pomůže",
    desc: "Klikneš na červené tlačítko, popíšeš svůj problém a všem profesionálům ve zvoleném oboru pípne telefon. Někdo ti rychle odpoví. Vzájemná pomoc, 24/7.",
    pushNotice: "ℹ️ Push notifikace (pípnutí telefonu) budou plně funkční až po vydání aplikace Tigik na Google Play — již brzy. Zatím uvidíš nová SOS po obnovení stránky.",
    placeholder: "Popiš svůj problém — buď konkrétní (svářečka, materiál, tloušťka, co jsi zkusil). Čím víc detailů, tím rychleji ti pomohou.",
    areaLabel: "Komu posíláš?",
    areaHint: "Vyber obor — pípne jen profíkům z této oblasti",
    levelLabel: "Tvoje úroveň",
    levelHint: "Otázka půjde profíkům stejné nebo vyšší úrovně",
    areas: { orbital: "Orbitálním svářečům", tig: "TIG svářečům", pipefitter: "Potrubářům", prep: "Přípravářům", other: "Ostatní profese" },
    levels: { beginner: "Začátečník", experienced: "Pokročilý", expert: "Profík / Expert" },
    send: "POSLAT SOS",
    sending: "Posílám...",
    minChars: "Napiš alespoň 5 znaků.",
    sent: "SOS odesláno! Pípne všem aktivním svářečům — někdo ti rychle pomůže.",
    error: "Nepodařilo se odeslat SOS.",
    recent: "Nejnovější SOS výzvy",
    noSos: "Zatím žádné aktivní SOS. Buď první pokud potřebuješ pomoc!",
    replies: "odpovědí",
    reply: "Odpovědět",
    replyPlaceholder: "Napiš jak mu pomůžeš...",
    replySend: "Odeslat",
    replied: "Odpověď odeslána!",
    yourSos: "Tvoje výzva",
    reportTitle: "Nahlásit tuto odpověď adminovi (zneužití / sranda)",
    reportConfirm: "Opravdu nahlásit tuto odpověď adminovi? Použij jen pokud si z tebe někdo dělá srandu nebo je hrubý.",
    reported: "Nahlášeno adminovi.",
    locked_start: "Online pomoc od komunity",
    locked_start_desc: "Kdybys měl ACTIVE nebo PRO, máš úplnou podporu od ostatních uživatelů v reálném čase. Klikneš, popíšeš problém, pípne celá komunita.",
    locked_cta: "Aktivovat ACTIVE",
    locked_free_desc: "Předplať si Tigik a získej okamžitou SOS pomoc od stovek svářečů v komunitě.",
    moments_ago: "před chvílí",
  },
  de: {
    title: "HIIIILFE",
    subtitle: "Dein SOS-Ruf — die ganze Community hilft",
    desc: "Klick auf den roten Knopf, beschreibe dein Problem und das Handy jedes Profis im gewählten Fachgebiet piept. Jemand antwortet schnell. Gegenseitige Hilfe, 24/7.",
    pushNotice: "ℹ️ Push-Benachrichtigungen (Handy-Piepen) funktionieren vollständig nach dem Release der Tigik-App im Google Play — bald verfügbar. Vorerst siehst du neue SOS-Rufe nach dem Neuladen der Seite.",
    placeholder: "Beschreibe dein Problem — sei konkret (Schweißgerät, Material, Dicke, was du versucht hast). Mehr Details = schnellere Hilfe.",
    areaLabel: "An wen senden?",
    areaHint: "Fachgebiet wählen — nur Profis aus diesem Bereich erhalten den Ping",
    levelLabel: "Dein Level",
    levelHint: "Frage geht an Profis mit gleichem oder höherem Level",
    areas: { orbital: "Orbital-Schweißer", tig: "WIG-Schweißer", pipefitter: "Rohrleger", prep: "Vorbereiter", other: "Andere Berufe" },
    levels: { beginner: "Anfänger", experienced: "Fortgeschritten", expert: "Profi / Experte" },
    send: "SOS SENDEN",
    sending: "Sende...",
    minChars: "Mindestens 5 Zeichen tippen.",
    sent: "SOS gesendet! Jedem aktiven Schweißer piept das Handy — jemand hilft schnell.",
    error: "SOS konnte nicht gesendet werden.",
    recent: "Aktuelle SOS-Rufe",
    noSos: "Noch keine aktiven SOS. Sei der Erste, falls du Hilfe brauchst!",
    replies: "Antworten",
    reply: "Antworten",
    replyPlaceholder: "Schreib, wie du helfen wirst...",
    replySend: "Senden",
    replied: "Antwort gesendet!",
    yourSos: "Dein Ruf",
    reportTitle: "Diese Antwort dem Admin melden (Missbrauch / Scherz)",
    reportConfirm: "Diese Antwort wirklich dem Admin melden? Nur verwenden, wenn jemand sich lustig macht oder unhöflich ist.",
    reported: "Dem Admin gemeldet.",
    locked_start: "Live-Hilfe von der Community",
    locked_start_desc: "Mit ACTIVE oder PRO bekommst du vollen Echtzeit-Support von anderen Nutzern. Klicken, Problem beschreiben, die ganze Community wird benachrichtigt.",
    locked_cta: "ACTIVE aktivieren",
    locked_free_desc: "Abonniere Tigik für sofortige SOS-Hilfe von Hunderten Schweißern in der Community.",
    moments_ago: "vor wenigen Augenblicken",
  },
  pl: {
    title: "POMOOOC",
    subtitle: "Twoje wezwanie SOS — cała społeczność ci pomoże",
    desc: "Klikasz czerwony przycisk, opisujesz problem i piszczy telefon każdemu profesjonaliście z wybranej dziedziny. Ktoś szybko odpowie. Wzajemna pomoc, 24/7.",
    pushNotice: "ℹ️ Powiadomienia push (piszczenie telefonu) będą w pełni działać po wydaniu aplikacji Tigik w Google Play — już wkrótce. Na razie nowe SOS zobaczysz po odświeżeniu strony.",
    placeholder: "Opisz problem — bądź konkretny (spawarka, materiał, grubość, co próbowałeś). Więcej szczegółów = szybsza pomoc.",
    areaLabel: "Do kogo wysyłasz?",
    areaHint: "Wybierz dziedzinę — piszczy tylko profesjonalistom z tego obszaru",
    levelLabel: "Twój poziom",
    levelHint: "Pytanie trafi do profesjonalistów równego lub wyższego poziomu",
    areas: { orbital: "Spawaczom orbitalnym", tig: "Spawaczom TIG", pipefitter: "Rurociągowcom", prep: "Przygotowywaczom", other: "Inne zawody" },
    levels: { beginner: "Początkujący", experienced: "Zaawansowany", expert: "Profesjonalista / Ekspert" },
    send: "WYŚLIJ SOS",
    sending: "Wysyłam...",
    minChars: "Napisz co najmniej 5 znaków.",
    sent: "SOS wysłane! Piszczy telefon każdego aktywnego spawacza — ktoś szybko pomoże.",
    error: "Nie udało się wysłać SOS.",
    recent: "Najnowsze wezwania SOS",
    noSos: "Brak aktywnych SOS. Bądź pierwszy jeśli potrzebujesz pomocy!",
    replies: "odpowiedzi",
    reply: "Odpowiedz",
    replyPlaceholder: "Napisz jak mu pomożesz...",
    replySend: "Wyślij",
    replied: "Odpowiedź wysłana!",
    yourSos: "Twoje wezwanie",
    reportTitle: "Zgłoś tę odpowiedź adminowi (nadużycie / żart)",
    reportConfirm: "Naprawdę zgłosić tę odpowiedź adminowi? Użyj tylko jeśli ktoś się z ciebie nabija lub jest niegrzeczny.",
    reported: "Zgłoszono adminowi.",
    locked_start: "Pomoc online od społeczności",
    locked_start_desc: "Gdybyś miał ACTIVE lub PRO, masz pełne wsparcie od innych użytkowników w czasie rzeczywistym. Klikasz, opisujesz problem, piszczy cała społeczność.",
    locked_cta: "Aktywuj ACTIVE",
    locked_free_desc: "Subskrybuj Tigik i uzyskaj natychmiastową pomoc SOS od setek spawaczy w społeczności.",
    moments_ago: "przed chwilą",
  },
  fr: {
    title: "AAAAAIDE",
    subtitle: "Ton appel SOS — toute la communauté t'aide",
    desc: "Tu cliques sur le bouton rouge, tu décris ton problème et le téléphone de tous les pros du domaine choisi va bipper. Quelqu'un te répondra vite. Entraide, 24/7.",
    pushNotice: "ℹ️ Les notifications push (bip du téléphone) seront pleinement fonctionnelles après la sortie de l'appli Tigik sur Google Play — très bientôt. En attendant, tu verras les nouveaux appels SOS dès que tu rafraîchis la page.",
    placeholder: "Décris ton problème — sois précis (machine à souder, matériau, épaisseur, ce que tu as essayé). Plus tu donnes de détails, plus vite on t'aide.",
    areaLabel: "À qui tu envoies ?",
    areaHint: "Choisis le domaine — seuls les pros de cette zone qui parlent ta langue vont bipper",
    levelLabel: "Ton niveau",
    levelHint: "La question ira aux pros de même niveau ou supérieur",
    areas: { orbital: "Soudeurs orbital", tig: "Soudeurs TIG", pipefitter: "Tuyauteurs", prep: "Préparateurs", other: "Autres métiers" },
    levels: { beginner: "Débutant", experienced: "Expérimenté", expert: "Pro / Expert" },
    send: "ENVOYER SOS",
    sending: "Envoi...",
    minChars: "Écris au moins 5 caractères.",
    sent: "SOS envoyé ! Tous les soudeurs actifs vont bipper — quelqu'un va t'aider vite.",
    error: "Impossible d'envoyer le SOS.",
    recent: "Derniers appels SOS",
    noSos: "Aucun SOS actif pour l'instant. Sois le premier si tu as besoin d'aide !",
    replies: "réponses",
    reply: "Répondre",
    replyPlaceholder: "Écris comment tu l'aides...",
    replySend: "Envoyer",
    replied: "Réponse envoyée !",
    yourSos: "Ton appel",
    reportTitle: "Signaler cette réponse à l'admin (abus / blague)",
    reportConfirm: "Vraiment signaler cette réponse à l'admin ? Utilise ça seulement si quelqu'un se moque de toi ou est grossier.",
    reported: "Signalé à l'admin.",
    locked_start: "Aide en ligne de la communauté",
    locked_start_desc: "Si tu avais ACTIVE ou PRO, tu aurais le soutien complet des autres utilisateurs en temps réel. Tu cliques, tu décris ton problème, toute la communauté bippe.",
    locked_cta: "Activer ACTIVE",
    locked_free_desc: "Abonne-toi à Tigik et obtiens une aide SOS immédiate de centaines de soudeurs de la communauté.",
    moments_ago: "à l'instant",
  },
};

function timeAgo(iso, lang) {
  if (!iso) return "";
  const dt = new Date(iso);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return (TR[lang] || TR.sk).moments_ago;
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h`;
  return dt.toLocaleDateString();
}

export default function SosCenter() {
  const { user } = useAuth();
  const { lang } = useLang();
  const tr = TR[lang] || TR.sk;
  const tier = (user?.plan_tier || "free").toLowerCase();
  const canUse = user?.is_subscribed && tier !== "start";

  const [text, setText] = useState("");
  const [area, setArea] = useState((user?.expertise_areas && user.expertise_areas[0]) || "tig");
  const [level, setLevel] = useState(user?.expertise_level || "experienced");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [replyDraft, setReplyDraft] = useState({}); // sosId -> text

  const load = async () => {
    if (!canUse) return;
    try {
      const r = await api.get("/sos");
      setItems(r.data.items || []);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    load();
    if (!canUse) return;
    const id = setInterval(load, 20000); // poll every 20s
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUse]);

  const submitSos = async () => {
    const t = text.trim();
    if (t.length < 5) { toast.error(tr.minChars); return; }
    setBusy(true);
    try {
      await api.post("/sos", { text: t, area, level });
      setText("");
      toast.success(tr.sent);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr.error);
    } finally {
      setBusy(false);
    }
  };

  const submitReply = async (sosId) => {
    const t = (replyDraft[sosId] || "").trim();
    if (!t) return;
    try {
      await api.post(`/sos/${sosId}/reply`, { text: t });
      setReplyDraft((s) => ({ ...s, [sosId]: "" }));
      toast.success(tr.replied);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr.error);
    }
  };

  const reportReply = async (sosId, replyId) => {
    if (!window.confirm(tr.reportConfirm)) return;
    try {
      await api.post(`/sos/${sosId}/reply/${replyId}/report`);
      toast.success(tr.reported);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr.error);
    }
  };

  // -------- LOCKED VARIANTS (START / FREE) --------
  if (!canUse) {
    const isStart = tier === "start";
    return (
      <div data-testid="sos-locked" className="border-2 border-red-700/40 bg-gradient-to-br from-red-950/30 via-zinc-950 to-zinc-950 p-6 mb-6 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-40 h-40 bg-red-500/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-red-500/20 border-2 border-red-500 flex items-center justify-center">
              <AlertOctagon className="w-6 h-6 text-red-400" strokeWidth={2} />
            </div>
            <div>
              <div className="font-display font-black text-xl uppercase tracking-wider text-red-400">{tr.title}</div>
              <div className="text-xs text-zinc-500">{tr.locked_start}</div>
            </div>
            <Lock className="w-4 h-4 text-zinc-600 ml-auto" />
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed mb-4">
            {isStart ? tr.locked_start_desc : tr.locked_free_desc}
          </p>
          <Link to="/pricing" data-testid="sos-locked-cta" className="wb-btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm">
            <Zap className="w-4 h-4" /> {tr.locked_cta}
          </Link>
        </div>
      </div>
    );
  }

  // -------- ACTIVE / PRO / GOLD / ADMIN — full SOS UI --------
  return (
    <div data-testid="sos-center" className="space-y-5 mb-6">
      <div className="border-2 border-red-700/50 bg-gradient-to-br from-red-950/40 via-zinc-950 to-zinc-950 p-6 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-48 h-48 bg-red-500/10 blur-3xl pointer-events-none animate-pulse" />
        <div className="relative">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-14 h-14 bg-red-500/20 border-2 border-red-500 flex items-center justify-center flex-shrink-0">
              <AlertOctagon className="w-7 h-7 text-red-400" strokeWidth={2.2} />
            </div>
            <div className="flex-1">
              <div className="font-display font-black text-2xl uppercase tracking-wider text-red-400 leading-none">{tr.title}</div>
              <div className="text-xs text-zinc-500 mt-1">{tr.subtitle}</div>
            </div>
          </div>
          <p className="text-xs text-zinc-400 leading-relaxed mb-4">{tr.desc}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">{tr.areaLabel}</label>
              <select
                value={area} onChange={(e) => setArea(e.target.value)}
                data-testid="sos-area-select"
                className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm text-white focus:border-red-500 outline-none"
              >
                {["orbital", "tig", "pipefitter", "prep", "other"].map((k) => (
                  <option key={k} value={k}>{tr.areas[k]}</option>
                ))}
              </select>
              <div className="text-[10px] text-zinc-600 mt-1">{tr.areaHint}</div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">{tr.levelLabel}</label>
              <select
                value={level} onChange={(e) => setLevel(e.target.value)}
                data-testid="sos-level-select"
                className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm text-white focus:border-red-500 outline-none"
              >
                {["beginner", "experienced", "expert"].map((k) => (
                  <option key={k} value={k}>{tr.levels[k]}</option>
                ))}
              </select>
              <div className="text-[10px] text-zinc-600 mt-1">{tr.levelHint}</div>
            </div>
          </div>
          <textarea
            value={text} onChange={(e) => setText(e.target.value.slice(0, 1500))}
            data-testid="sos-text-input" rows={3}
            placeholder={tr.placeholder}
            className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-red-500 outline-none resize-none mb-3"
          />
          <div className="flex items-center justify-between">
            <div className="text-[10px] text-zinc-600">{text.length}/1500</div>
            <button
              onClick={submitSos} disabled={busy || text.trim().length < 5}
              data-testid="sos-submit-btn"
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white px-6 py-3 font-display font-black uppercase tracking-widest text-sm inline-flex items-center gap-2 disabled:opacity-40 shadow-[0_0_24px_-6px_rgba(239,68,68,0.7)]"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {busy ? tr.sending : tr.send}
            </button>
          </div>
        </div>
      </div>

      {/* SOS feed */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">{tr.recent}</div>
        {items.length === 0 ? (
          <div className="text-xs text-zinc-600 italic">{tr.noSos}</div>
        ) : (
          <div className="space-y-3" data-testid="sos-feed">
            {items.map((s) => {
              const isMine = user?.id === s.user_id;
              return (
                <div key={s.id} className="border border-zinc-800 bg-zinc-950 p-4" data-testid={`sos-item-${s.id}`}>
                  <div className="flex items-start gap-3 mb-2">
                    {s.user_picture
                      ? <img src={s.user_picture} alt="" className="w-9 h-9 object-cover rounded-full flex-shrink-0" />
                      : <div className="w-9 h-9 bg-zinc-800 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{(s.user_name || "?")[0]}</div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold text-sm text-white">{s.user_name}</span>
                        {s.user_plan_tier === "gold" && <GoldBadge size="sm" />}
                        {s.area && (
                          <span data-testid={`sos-area-tag-${s.id}`} className="text-[9px] font-mono uppercase tracking-widest text-red-300 border border-red-700/50 px-1.5 py-0.5 bg-red-950/30">
                            {tr.areas[s.area] || s.area}
                          </span>
                        )}
                        {s.level && (
                          <span data-testid={`sos-level-tag-${s.id}`} className="text-[9px] font-mono uppercase tracking-widest text-zinc-400 border border-zinc-700 px-1.5 py-0.5">
                            {tr.levels[s.level] || s.level}
                          </span>
                        )}
                        {isMine && <span className="text-[10px] font-mono uppercase tracking-widest text-red-400 border border-red-700/50 px-1.5 py-0.5">{tr.yourSos}</span>}
                        <span className="text-[10px] text-zinc-500 ml-auto">{timeAgo(s.created_at, lang)}</span>
                      </div>
                      <div className="text-sm text-zinc-200 leading-relaxed mt-1.5 whitespace-pre-wrap break-words">{s.text}</div>
                    </div>
                  </div>

                  {/* Replies */}
                  {(s.replies || []).length > 0 && (
                    <div className="mt-3 pl-3 border-l border-zinc-800 space-y-2">
                      {s.replies.map((r) => (
                        <div key={r.id} className="bg-zinc-900/50 p-2.5" data-testid={`sos-reply-${r.id}`}>
                          <div className="flex items-center gap-1.5 flex-wrap text-xs">
                            <span className="font-display font-bold text-white">{r.user_name}</span>
                            {r.user_certified && <CertifiedBadge size="sm" certificateNo={r.user_certificate_no} />}
                            {r.user_certified && r.user_certified_since && <LifetimeMedal since={r.user_certified_since} lang={lang} size="xs" />}
                            {r.user_plan_tier === "gold" && <GoldBadge size="sm" />}
                            <span className="text-[10px] text-zinc-500 ml-auto">{timeAgo(r.created_at, lang)}</span>
                            {isMine && !(r.reported_by || []).includes(user.id) && (
                              <button
                                onClick={() => reportReply(s.id, r.id)}
                                title={tr.reportTitle} data-testid={`sos-report-${r.id}`}
                                className="text-zinc-600 hover:text-red-400 transition-colors"
                              >
                                <Flag className="w-3 h-3" />
                              </button>
                            )}
                            {(r.reported_by || []).includes(user?.id) && (
                              <span className="text-[10px] font-mono text-red-400">⚠ {tr.reported.replace(".", "")}</span>
                            )}
                          </div>
                          <div className="text-sm text-zinc-300 leading-relaxed mt-1 whitespace-pre-wrap break-words">{r.text}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply box */}
                  <div className="mt-3 flex items-center gap-2">
                    <MessageCircle className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" />
                    <input
                      type="text" value={replyDraft[s.id] || ""}
                      onChange={(e) => setReplyDraft((st) => ({ ...st, [s.id]: e.target.value.slice(0, 1500) }))}
                      onKeyDown={(e) => { if (e.key === "Enter") submitReply(s.id); }}
                      placeholder={tr.replyPlaceholder}
                      data-testid={`sos-reply-input-${s.id}`}
                      className="flex-1 bg-black border border-zinc-800 px-3 py-1.5 text-xs text-white placeholder-zinc-600 focus:border-gold-600 outline-none"
                    />
                    <button onClick={() => submitReply(s.id)} disabled={!(replyDraft[s.id] || "").trim()}
                      data-testid={`sos-reply-send-${s.id}`}
                      className="wb-btn-primary px-3 py-1.5 text-xs disabled:opacity-40">
                      {tr.replySend}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
